import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  ButtonBase
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SwapVertRounded from "@mui/icons-material/SwapVertRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import { MARKETPLACE } from "@/data/mock-data";
import type { MarketplaceTemplate } from "@/data/mock-data";
import { BotsMarketplaceToggle } from "@/components/BotsMarketplaceToggle";
import { EmptyState } from "@/components/EmptyState";
import { MarketplaceDetailDrawer } from "@/components/MarketplaceDetailDrawer";
import { useCanAccessMarketplace } from "@/lib/features";
import { useBotActionsStore } from "@/store/bot-actions-store";
import {
  MarketplaceSortSheet,
  MarketplaceFilterSheet,
  applyMarketplaceFilters,
  activeFilterCount,
  DEFAULT_MARKETPLACE_FILTER,
  MARKETPLACE_SORT_OPTIONS,
  type MarketplaceFilterState
} from "@/components/MarketplaceFilters";

/** Every category tag present across the template set. */
const ALL_CATEGORIES = Array.from(
  new Set(MARKETPLACE.flatMap((t) => t.tags))
).sort();

export function MarketplacePage() {
  const canAccessMarketplace = useCanAccessMarketplace();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MarketplaceFilterState>(
    DEFAULT_MARKETPLACE_FILTER
  );
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  // Tapping a row opens the detail drawer in place (Sergey 2026-05-13:
  // drawers, not pages, for sub-views).
  const [detail, setDetail] = useState<MarketplaceTemplate | null>(null);

  // All / Watch List segment — kept in the URL (`?view=watch`) so the
  // "Go to My Watch List" toast link can deep-link straight here
  // (Sergey 2026-05-21).
  const [searchParams, setSearchParams] = useSearchParams();
  const view: "all" | "watch" =
    searchParams.get("view") === "watch" ? "watch" : "all";
  const setView = (v: "all" | "watch") => {
    const next = new URLSearchParams(searchParams);
    if (v === "watch") next.set("view", "watch");
    else next.delete("view");
    setSearchParams(next, { replace: true });
  };
  const watchedIds = useBotActionsStore((s) => s.watchedIds);
  const watchedCount = useMemo(
    () => MARKETPLACE.filter((t) => watchedIds.includes(t.id)).length,
    [watchedIds]
  );

  const results = useMemo(() => {
    const sorted = applyMarketplaceFilters(MARKETPLACE, filter, search);
    return view === "watch"
      ? sorted.filter((t) => watchedIds.includes(t.id))
      : sorted;
  }, [filter, search, view, watchedIds]);

  const sortLabel =
    MARKETPLACE_SORT_OPTIONS.find((o) => o.key === filter.sort)?.label ??
    "Sort";
  const filterCount = activeFilterCount(filter);

  // Feature gate — Marketplace is entitlement-gated. The view-tab
  // toggle already hides the tab for users who can't access it; this
  // guards a direct navigation to /marketplace.
  if (!canAccessMarketplace) {
    return (
      <Box sx={{ maxWidth: 680, mx: "auto", px: 2, pt: 1 }}>
        <BotsMarketplaceToggle />
        <EmptyState
          icon={<StorefrontOutlined />}
          title="Marketplace isn't available"
          body="The strategy Marketplace isn't included on your current plan."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: 2, pt: 1 }}>
      <BotsMarketplaceToggle />

      <Stack spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h2">Strategy Bots Marketplace</Typography>
        <Typography variant="body2" color="text.secondary">
          Browse example bot templates created by WHB and the community.
          Customize the assets, risk parameters, and rules before running
          one in your own account.
        </Typography>
      </Stack>

      {/* All / Watch List segment */}
      <Box
        sx={(t) => ({
          display: "inline-flex",
          p: 0.5,
          mb: 1.5,
          gap: 0.5,
          borderRadius: 2,
          bgcolor: t.palette.action.hover
        })}
      >
        {(
          [
            { key: "all", label: "All bots" },
            {
              key: "watch",
              label: `Watch list${watchedCount ? ` (${watchedCount})` : ""}`
            }
          ] as const
        ).map((seg) => {
          const active = view === seg.key;
          return (
            <ButtonBase
              key={seg.key}
              onClick={() => setView(seg.key)}
              sx={(t) => ({
                px: 1.75,
                py: 0.6,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: "0.8125rem",
                color: active
                  ? t.palette.text.primary
                  : t.palette.text.secondary,
                bgcolor: active ? t.palette.background.paper : "transparent",
                boxShadow: active ? 1 : 0
              })}
            >
              {seg.label}
            </ButtonBase>
          );
        })}
      </Box>

      {/* Search + Sort + Filter controls */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search bots, creators…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchRounded sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 18,
              fontSize: "1rem",
              height: 36,
              bgcolor: "background.paper"
            },
            "& .MuiOutlinedInput-input": { py: 0.25 }
          }}
        />
        <IconButton
          aria-label="Sort"
          onClick={() => setSortOpen(true)}
          sx={(t) => ({
            border: `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            flexShrink: 0
          })}
        >
          <SwapVertRounded sx={{ fontSize: 20 }} />
        </IconButton>
        <IconButton
          aria-label="Filters"
          onClick={() => setFilterOpen(true)}
          sx={(t) => ({
            border: `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            flexShrink: 0,
            color: filterCount > 0 ? "primary.main" : undefined,
            borderColor:
              filterCount > 0 ? t.palette.primary.main : t.palette.divider
          })}
        >
          <TuneRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      {/* Active sort/filter summary chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Chip
          size="small"
          label={`Sorted by ${sortLabel}`}
          onClick={() => setSortOpen(true)}
          sx={{ fontWeight: 600 }}
        />
        {filterCount > 0 && (
          <Chip
            size="small"
            label={`${filterCount} filter${filterCount === 1 ? "" : "s"}`}
            onClick={() => setFilterOpen(true)}
            onDelete={() =>
              setFilter({ ...filter, recoveryTime: "any", categories: [] })
            }
            sx={{ fontWeight: 600 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: "center" }}
        >
          {results.length} bot{results.length === 1 ? "" : "s"}
        </Typography>
      </Stack>

      {results.length === 0 && view === "watch" && watchedCount === 0 ? (
        <EmptyState
          icon={<VisibilityOutlined />}
          title="No bots in your watch list"
          body="Tap Watch on any Marketplace bot to track it on a paper account — it'll show up here."
          actions={[{ label: "Browse all bots", onClick: () => setView("all") }]}
        />
      ) : results.length === 0 ? (
        <EmptyState
          icon={<SearchRounded />}
          title="No bots match"
          body="Try a different search term or loosen the filters."
          actions={[
            {
              label: "Clear filters",
              onClick: () => {
                setSearch("");
                setFilter(DEFAULT_MARKETPLACE_FILTER);
              }
            }
          ]}
        />
      ) : (
        <Box>
          {results.map((tpl, i) => (
            <Box key={tpl.id}>
              {i > 0 && <Divider />}
              <ButtonBase
                onClick={() => setDetail(tpl)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  py: 1.5,
                  "&:hover": { bgcolor: "action.hover" }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                  {tpl.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  By {tpl.creator}
                </Typography>
                {/* Three headline metrics on each row. */}
                <Box
                  sx={{
                    mt: 1,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    columnGap: 1.5
                  }}
                >
                  <Metric
                    label="Cumulative"
                    value={`${formatPct(tpl.cumulativeReturn)}`}
                  />
                  <Metric
                    label="Annualized"
                    value={`${formatPct(tpl.ar)}`}
                  />
                  <Metric
                    label="Max Drawdown"
                    value={`−${tpl.maxDrawdown.toFixed(1)}%`}
                    negative
                  />
                </Box>
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}

      <MarketplaceSortSheet
        open={sortOpen}
        value={filter.sort}
        onChange={(sort) => setFilter({ ...filter, sort })}
        onClose={() => setSortOpen(false)}
      />
      <MarketplaceFilterSheet
        open={filterOpen}
        value={filter}
        allCategories={ALL_CATEGORIES}
        onChange={setFilter}
        onClose={() => setFilterOpen(false)}
      />
      <MarketplaceDetailDrawer
        template={detail}
        onClose={() => setDetail(null)}
      />
    </Box>
  );
}

/** Compact "label over value" cell for the marketplace row metrics. */
function Metric({
  label,
  value,
  negative
}: {
  label: string;
  value: string;
  negative?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={(t) => ({
          fontWeight: 700,
          fontFeatureSettings: '"tnum" 1',
          color: negative ? t.palette.error.main : t.palette.text.primary
        })}
        noWrap
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Format a positive percentage figure with thousands separators. */
function formatPct(n: number): string {
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}
